import React, { useState } from "react";
import { BarChart3Icon, BriefcaseIcon, Building2Icon, CalculatorIcon, CalendarClockIcon, CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon, ClipboardListIcon, FileSignatureIcon, FileTextIcon, FolderIcon, HandCoinsIcon, LandmarkIcon, LayoutDashboardIcon, LockIcon, MenuIcon, ReceiptIcon, SettingsIcon, UsersIcon, WalletIcon, XIcon, BoxIcon } from "lucide-react";
import { useData } from "../../../store/DataContext";
import { HrPayrollProvider, useHrPayroll } from "../../../contexts/HrPayrollContext";
import { HrOverview } from "./HrOverview";
import { EmployeeManagement } from "./EmployeeManagement";
import { DepartmentsPositions } from "./DepartmentsPositions";
import { EmploymentContracts } from "./EmploymentContracts";
import { AttendanceShifts } from "./AttendanceShifts";
import { LeaveManagement } from "./LeaveManagement";
import { SalaryAllowances } from "./SalaryAllowances";
import { LoansAdvances } from "./LoansAdvances";
import { PayrollProcessing } from "./PayrollProcessing";
import { EmployeePayslips } from "./EmployeePayslips";
import { StatutoryContributions } from "./StatutoryContributions";
import { PayrollReports } from "./PayrollReports";
import { HrDocuments } from "./HrDocuments";
import { PayrollSettingsPage } from "./PayrollSettingsPage";
import { HrAuditLogs } from "./HrAuditLogs";
export type HrSection = 'overview' | 'employees' | 'departments' | 'contracts' | 'attendance' | 'leave' | 'salary' | 'loans' | 'payroll' | 'payslips' | 'statutory' | 'reports' | 'documents' | 'settings' | 'audit';
type NavGroup = {
  label: string;
  items: {
    id: HrSection;
    label: string;
    icon: BoxIcon;
  }[];
};
const navGroups: NavGroup[] = [{
  label: 'Overview',
  items: [{
    id: 'overview',
    label: 'HR & Payroll Overview',
    icon: LayoutDashboardIcon
  }]
}, {
  label: 'People',
  items: [{
    id: 'employees',
    label: 'Employee Management',
    icon: UsersIcon
  }, {
    id: 'departments',
    label: 'Departments & Positions',
    icon: Building2Icon
  }, {
    id: 'contracts',
    label: 'Employment Contracts',
    icon: FileSignatureIcon
  }, {
    id: 'documents',
    label: 'HR Documents',
    icon: FolderIcon
  }]
}, {
  label: 'Time',
  items: [{
    id: 'attendance',
    label: 'Attendance & Duty Shifts',
    icon: CalendarClockIcon
  }, {
    id: 'leave',
    label: 'Leave Management',
    icon: CalendarDaysIcon
  }]
}, {
  label: 'Pay',
  items: [{
    id: 'salary',
    label: 'Salary & Allowances',
    icon: WalletIcon
  }, {
    id: 'loans',
    label: 'Employee Loans & Advances',
    icon: HandCoinsIcon
  }, {
    id: 'payroll',
    label: 'Payroll Processing',
    icon: CalculatorIcon
  }, {
    id: 'payslips',
    label: 'Employee Payslips',
    icon: ReceiptIcon
  }]
}, {
  label: 'Compliance',
  items: [{
    id: 'statutory',
    label: 'Statutory Contributions',
    icon: LandmarkIcon
  }, {
    id: 'reports',
    label: 'Payroll Reports',
    icon: BarChart3Icon
  }, {
    id: 'audit',
    label: 'Audit Logs',
    icon: ClipboardListIcon
  }, {
    id: 'settings',
    label: 'Payroll Settings',
    icon: SettingsIcon
  }]
}];
const allItems = navGroups.flatMap(group => group.items);

/** System Administrator only — guarded here as well as in navigation. */
export function HrPayrollModule() {
  const {
    currentUser
  } = useData();
  if (currentUser?.role !== 'Administrator') {
    return <div className="mx-auto mt-16 max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm" data-id="element-1025" data-mp="3uwsexn1ebbft">
        <LockIcon className="mx-auto h-10 w-10 text-slate-400" aria-hidden="true" data-id="element-1026" data-mp="3od62xvd8rix" />
        <h1 className="mt-3 text-lg font-bold text-slate-900" data-id="element-1027" data-mp="7scl1c0zzia6">Access denied</h1>
        <p className="mt-1 text-sm text-slate-600" data-id="element-1028" data-mp="cqw85frbv044">Human Resource &amp; Payroll is restricted to the System Administrator.</p>
      </div>;
  }
  return <HrPayrollProvider data-id="element-1029" data-mp="29k7iammibk8v">
      <HrPayrollShell data-id="element-1030" data-mp="29l0qls146ht4" />
    </HrPayrollProvider>;
}
function HrPayrollShell() {
  const {
    isReady
  } = useHrPayroll();
  const [section, setSection] = useState<HrSection>('overview');
  const [profileId, setProfileId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const select = (next: HrSection) => {
    setSection(next);
    setMobileOpen(false);
    if (next !== 'employees') setProfileId(null);
  };
  const openEmployee = (id: string) => {
    setProfileId(id);
    setSection('employees');
  };
  const current = allItems.find(item => item.id === section)!;
  const renderContent = () => {
    if (!isReady) return <div role="status" className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-600" data-id="element-1031" data-mp="1a6mg9ftqannq">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" aria-hidden="true" data-id="element-1032" data-mp="15f4nvg5rtved" />
          Loading HR &amp; payroll records…
        </div>;
    switch (section) {
      case 'overview':
        return <HrOverview onNavigate={select} data-id="element-1033" data-mp="cpo1p08qvaql" />;
      case 'employees':
        return <EmployeeManagement profileId={profileId} onOpenProfile={setProfileId} data-id="element-1034" data-mp="2od9f14880bo7" />;
      case 'departments':
        return <DepartmentsPositions data-id="element-1035" data-mp="2t7ncdc6g0svi" />;
      case 'contracts':
        return <EmploymentContracts onOpenEmployee={openEmployee} data-id="element-1036" data-mp="2ehpicflu18ye" />;
      case 'attendance':
        return <AttendanceShifts data-id="element-1037" data-mp="2jgspc5ldhp90" />;
      case 'leave':
        return <LeaveManagement data-id="element-1038" data-mp="2jdepb406ls4q" />;
      case 'salary':
        return <SalaryAllowances onOpenEmployee={openEmployee} data-id="element-1039" data-mp="376mzgfeb4zpk" />;
      case 'loans':
        return <LoansAdvances data-id="element-1040" data-mp="3c3dfj2q43vaz" />;
      case 'payroll':
        return <PayrollProcessing data-id="element-1041" data-mp="3c229sxd3wdh8" />;
      case 'payslips':
        return <EmployeePayslips data-id="element-1042" data-mp="2y3vp6ix649zi" />;
      case 'statutory':
        return <StatutoryContributions data-id="element-1043" data-mp="325pkscsianst" />;
      case 'reports':
        return <PayrollReports data-id="element-1044" data-mp="328bpm4o0ip7c" />;
      case 'documents':
        return <HrDocuments data-id="element-1045" data-mp="1mofb78cx1w4m" />;
      case 'settings':
        return <PayrollSettingsPage data-id="element-1046" data-mp="1mruqocai576k" />;
      case 'audit':
        return <HrAuditLogs data-id="element-1047" data-mp="1qrvaydka8clj" />;
    }
  };
  const navigation = (mobile = false) => <nav className="space-y-5" aria-label="HR & Payroll navigation" data-id="element-1048" data-mp="3teaeerbm70yv">
      {navGroups.map(group => <div key={group.label} data-id="element-1049" data-mp="3oe4g4sauag7t">
          <p className={`chrome-label mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] ${collapsed && !mobile ? 'sr-only' : ''}`} data-id="element-1050" data-mp="3ofz0a4eae4au">{group.label}</p>
          <div className="space-y-0.5" data-id="element-1051" data-mp="3ep4c2uhhcumr">
            {group.items.map(item => {
          const Icon = item.icon;
          const active = section === item.id;
          return <button key={item.id} type="button" onClick={() => select(item.id)} title={collapsed && !mobile ? item.label : undefined} aria-current={active ? 'page' : undefined} className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${active ? 'chrome-nav-item-active' : 'chrome-nav-item'} ${collapsed && !mobile ? 'justify-center px-2' : ''}`} data-id="element-1052" data-mp="3al6dqlzzbnur">
                  <Icon className={`h-4 w-4 shrink-0 ${collapsed && !mobile ? '' : 'mr-3'}`} aria-hidden="true" data-id="element-1053" data-mp="ywxaul5qoc8e" />
                  {(!collapsed || mobile) && <span className="truncate" data-id="element-1054" data-mp="12fw2k4h3bw2r">{item.label}</span>}
                </button>;
        })}
          </div>
        </div>)}
    </nav>;
  const brand = <div className="flex items-center gap-3" data-id="element-1055" data-mp="16foi514wj2hm">
      <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-9 w-9 rounded-lg bg-white object-contain" data-id="element-1056" data-mp="1b87ujururrku" />
      {(!collapsed || mobileOpen) && <div className="min-w-0" data-id="element-1057" data-mp="jha2msxb8b0f">
          <p className="truncate text-sm font-bold" data-id="element-1058" data-mp="ji3a5w43f4jm">BMED MEDICAL CENTRE</p>
          <p className="chrome-label truncate text-xs font-medium" data-id="element-1059" data-mp="nhlno6tl4dcf">Human Resource &amp; Payroll</p>
        </div>}
    </div>;
  return <div className="-m-4 flex min-h-[calc(100vh-4rem)] bg-slate-50 sm:-m-6 lg:-m-8" data-id="element-1060" data-mp="sfviggbedbxi">
      <aside className={`chrome-sidebar chrome-divider hidden shrink-0 border-r lg:flex lg:flex-col ${collapsed ? 'w-[76px]' : 'w-[264px]'} transition-[width] duration-200`} data-id="element-1061" data-mp="xhkxs85nkbre">
        <div className={`chrome-divider border-b ${collapsed ? 'p-3' : 'p-5'}`} data-id="element-1062" data-mp="28v6se8ua75kb">
          {brand}
          {!collapsed && <div className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-white/20 px-2.5 py-1 text-[11px] font-semibold" data-id="element-1063" data-mp="2u0shkoun160t">
              <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-1064" data-mp="2q0075sa14v2y" /> System Admin only
            </div>}
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-5" data-id="element-1065" data-mp="33rn4lvfpk9nb">{navigation()}</div>
        <div className="chrome-divider border-t p-3" data-id="element-1066" data-mp="2yxajzi2hws9z">
          <button type="button" onClick={() => setCollapsed(v => !v)} className="chrome-icon-button flex w-full items-center justify-center rounded-md py-2 transition-colors" aria-label={collapsed ? 'Expand HR navigation' : 'Collapse HR navigation'} data-id="element-1067" data-mp="3dn8ba6vq1y3e">
            {collapsed ? <ChevronRightIcon className="h-4 w-4" data-id="element-1068" data-mp="3mmbst6f8q5fs" /> : <ChevronLeftIcon className="h-4 w-4" data-id="element-1069" data-mp="3hp3bf81x25fl" />}
          </button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 z-50 flex lg:hidden" data-id="element-1070" data-mp="1sbgbnfpzpi1e">
          <button type="button" aria-label="Close HR navigation" className="absolute inset-0 bg-slate-950/35" onClick={() => setMobileOpen(false)} data-id="element-1071" data-mp="1nbburol66lan" />
          <aside className="chrome-sidebar relative flex h-full w-[280px] flex-col shadow-2xl" data-id="element-1072" data-mp="2ukpjfxuyu7k">
            <div className="chrome-divider flex items-center justify-between border-b p-5" data-id="element-1073" data-mp="7r1g0gsj5knt">
              {brand}
              <button type="button" onClick={() => setMobileOpen(false)} className="chrome-icon-button rounded-md p-2" aria-label="Close HR navigation" data-id="element-1074" data-mp="3q5u4ebzsynqt">
                <XIcon className="h-5 w-5" data-id="element-1075" data-mp="3g7mqy34q2ntk" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4" data-id="element-1076" data-mp="3l792870zptzq">{navigation(true)}</div>
          </aside>
        </div>}

      <section className="min-w-0 flex-1" data-id="element-1077" data-mp="377zpjhbzti0i">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6 lg:px-8" data-id="element-1078" data-mp="3792h7rh53ohh">
          <div className="mx-auto flex max-w-[1600px] items-center gap-3" data-id="element-1079" data-mp="3bawctlcha2as">
            <button type="button" onClick={() => setMobileOpen(true)} className="rounded-md border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 lg:hidden" aria-label="Open HR navigation" data-id="element-1080" data-mp="15gmpp6ovu0bb">
              <MenuIcon className="h-5 w-5" data-id="element-1081" data-mp="1wo89ia7w3rb4" />
            </button>
            <div className="min-w-0" data-id="element-1082" data-mp="21gt2j9iz20dl">
              <p className="mb-0.5 text-xs font-medium text-slate-500" data-id="element-1083" data-mp="21huhhp33z70l">
                System Admin <span className="mx-1 text-slate-300" data-id="element-1084" data-mp="1mt80zk1qw97s">/</span> Human Resource &amp; Payroll
              </p>
              <h1 className="flex items-center gap-2 truncate text-lg font-bold text-slate-900 sm:text-xl" data-id="element-1085" data-mp="1rqzswe2e3vbz">
                <BriefcaseIcon className="hidden h-5 w-5 text-slate-400 sm:block" aria-hidden="true" data-id="element-1086" data-mp="2fe7xsuvwp2dh" />
                {current.label}
              </h1>
            </div>
            <span className="ml-auto hidden items-center gap-1.5 text-xs font-medium text-slate-500 md:inline-flex" data-id="element-1087" data-mp="26eul7tchjxoj">
              <FileTextIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1088" data-mp="2afeiqy0z13b7" /> Confidential
            </span>
          </div>
        </header>
        <main className="mx-auto max-w-[1600px] p-4 sm:p-6 lg:p-8" data-id="element-1089" data-mp="z22n3afd7r04">{renderContent()}</main>
      </section>
    </div>;
}